// Receive stock's draft (docs/plans/dialog-safety.md): what was typed, kept on this device by
// `lib/drafts.ts` while the dialog is open. Product ids and typed values only; the dialog reads
// the products again when the draft is continued. Pure, unit-tested.
import { parseSerials } from '@ims/core'

export interface ReceiveDraftLine {
  productId: string
  qtyMilli: number | null
  costPaisa: number | null
  batchNo: string
  mfgDate: string
  expiryDate: string
  expiryAuto: boolean
  mrpPaisa: number | null
  serialsText: string
  /** The purchase order line it fills. */
  orderLineId: string | null
}

/** The supplier's bill as typed (`BillDraft` in screens/supplier-bills.tsx). */
export interface ReceiveDraftBill {
  billDate: string
  dueDate: string
  totalPaisa: number | null
  paidPaisa: number | null
  method: string
}

export interface ReceiveDraft {
  supplierId: string
  locationId: string | null
  reference: string
  bill: ReceiveDraftBill
  billTyped: boolean
  lines: ReceiveDraftLine[]
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
const str = (v: unknown) => (typeof v === 'string' ? v : '')
const strOrNull = (v: unknown) => (typeof v === 'string' && v ? v : null)
const int = (v: unknown) => (typeof v === 'number' && Number.isSafeInteger(v) ? v : null)

function parseLine(v: unknown): ReceiveDraftLine | null {
  const l = obj(v)
  if (!l || typeof l.productId !== 'string' || !l.productId) return null
  return {
    productId: l.productId,
    qtyMilli: int(l.qtyMilli),
    costPaisa: int(l.costPaisa),
    batchNo: str(l.batchNo),
    mfgDate: str(l.mfgDate),
    expiryDate: str(l.expiryDate),
    expiryAuto: l.expiryAuto === true,
    mrpPaisa: int(l.mrpPaisa),
    serialsText: str(l.serialsText),
    orderLineId: strOrNull(l.orderLineId),
  }
}

/** The draft as read from storage, or null when it is not one. */
export function parseReceiveDraft(v: unknown): ReceiveDraft | null {
  const d = obj(v)
  const bill = obj(d?.bill)
  if (!d || !bill || !Array.isArray(d.lines)) return null
  const lines = d.lines.map(parseLine).filter((l): l is ReceiveDraftLine => l !== null)
  return {
    supplierId: str(d.supplierId),
    locationId: strOrNull(d.locationId),
    reference: str(d.reference),
    bill: {
      billDate: str(bill.billDate),
      dueDate: str(bill.dueDate),
      totalPaisa: int(bill.totalPaisa),
      paidPaisa: int(bill.paidPaisa),
      method: str(bill.method) || 'cash',
    },
    billTyped: d.billTyped === true,
    lines,
  }
}

/** Worth keeping: a line, or a bill number typed. */
export function draftHasWork(d: ReceiveDraft): boolean {
  return d.lines.length > 0 || d.reference.trim() !== ''
}

const lineQty = (l: ReceiveDraftLine) =>
  l.serialsText.trim() ? parseSerials(l.serialsText).serials.length * 1000 : (l.qtyMilli ?? 0)

/** What the lines cost, as the dialog's footer adds it up. */
export function draftTotal(d: ReceiveDraft): number {
  return d.lines.reduce((a, l) => a + Math.round((lineQty(l) * (l.costPaisa ?? 0)) / 1000), 0)
}

/**
 * The draft's lines with their products as they are now. A product that is gone or no longer
 * active is left out (the caller passes only those), and so is a purchase order line no longer
 * due (`due`, when receiving against an order).
 */
export function restoreLines<P extends { id: string }>(
  d: ReceiveDraft,
  products: ReadonlyMap<string, P>,
  due: ReadonlySet<string> | null,
): { lines: (ReceiveDraftLine & { product: P })[]; dropped: number } {
  const lines: (ReceiveDraftLine & { product: P })[] = []
  for (const l of d.lines) {
    const product = products.get(l.productId)
    if (!product) continue
    // Something that came without being on the order has no order line: it stays.
    if (due && l.orderLineId && !due.has(l.orderLineId)) continue
    lines.push({ ...l, product })
  }
  return { lines, dropped: d.lines.length - lines.length }
}
